import { get, isUndefined, chunk, without, range, fill, merge, find } from 'lodash';
import React, { PropTypes } from 'react';
import TextField from './c_TextField';
import CheckList from './c_CheckList';
import DropDown from './SimpleDropDownMenu';
import PhoneNumberField from './PhoneNumberField';
import Button from './Button';

export default function AddContact(props) {

  const { model, staticData, fields, labels, disableSave, onSave, onCancel, onUpdate, columns, emailDuplicate } = props;
  const { contactTypes, timezoneList, contactPhoneTypes, stateList } = staticData;

  let mFields = fields.slice(0), mLabels = labels.slice(0);

  const mainPhone = find(contactPhoneTypes, {name: 'Main'});
  const mainPhoneId = get(mainPhone, 'id');

  if(get(model, 'user.phoneType1.value') !== mainPhoneId) {
    mFields = without(fields, 'user.phoneExtension1');
    mLabels = without(labels, 'Extension');
  }

  // How many empty columns we need to complete the last row with 3 columns.
  const generateBlankColumns = (cols) => fill(range(cols), '');
  const blankColumnsInLastRow = columns - (mFields.length % columns);
  const emptyColumns = generateBlankColumns(blankColumnsInLastRow);

  const processedFields =  [...mFields, ...emptyColumns];

  const fieldGroups = chunk(processedFields, columns);


  function handleUpdate(prop) {
    return (event, value) => {
      if (onUpdate) {
        onUpdate(event, prop, value);
      }
    };
  }

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  function renderPlaceHolder() {
    return (
      <div>&nbsp;</div>
    );
  }

  function renderDropdown(attributes) {
    const {
      onUpdate: onChange,
      ...attribs
    } = attributes;

    return (
      <DropDown
        iconStyle={{right: '0px', fill: 'rgb(33, 35, 38)'}}
        labelStyle={{paddingLeft: '0px'}}
        underlineStyle={{margin: '0px 0px'}}
        onChange={onChange}
        {
          ...attribs
        }
      />
    );
  }

  function renderChecklist(attributes) {
    const {
      onUpdate: onChange,
      ...attribs
    } = attributes;

    return (
      <CheckList
        className='dropdown-filter'
        onChange={onChange}
        {
          ...attribs
        }
      />
    );
  }

  function renderTextField(attributes) {
    const {
      onUpdate: onBlur,
      ...attribs
    } = attributes;

    return (
      <TextField
        onBlur={onBlur}
        onChange={props.onChange}
        {
          ...attribs
        }
      />
    );
  }

  function renderPhoneNumberField(attributes) {
    const {
      onUpdate: onBlur,
      ...attribs
    } = attributes;

    return (
      <PhoneNumberField
        onBlur={onBlur}
        onChange={props.onChange}
        {
          ...attribs
        }
      />
    );
  }

  function getFieldRenderer(type) {
    switch (type) {
      case 'TEXTFIELD':
        return renderTextField;
      case 'PHONENUMBER':
        return renderPhoneNumberField;
      case 'CHECKLIST':
        return renderChecklist;
      case 'DROPDOWN':
        return renderDropdown;
      default:
        return renderPlaceHolder;
    }
  }

  function getListItems(field) {
    if (field === 'profile.types') {
      return contactTypes;
    } else if (field === 'user.timezone') {
      return timezoneList;
    } else if (field === 'user.phoneType1') {
      return contactPhoneTypes;
    } else if (field === 'user.state') {
      return stateList;
    }
  }

  function getFieldType(field) {
    if (['profile.types'].includes(field)) {
      return 'CHECKLIST';
    }
    else if (['user.timezone', 'user.phoneType1', 'user.state'].includes(field)) {
      return 'DROPDOWN';
    }
    else if (['user.phoneNumber1', 'user.phoneNumber2'].includes(field)) {
      return 'PHONENUMBER';
    }
    else if(field.length === 0) {
      return 'PLACEHOLDER';
    }

    return 'TEXTFIELD';
  }

  function renderField(field, label, model) {
    const fieldType = getFieldType(field);
    const dirty = !isUndefined(get(model, `${field}.value`));

    const attributes = {
        key: `${field}_`,
        'data-id': field,
        defaultValue: get(model, `${field}.defaultValue`) || get(model, `${field}.value`),
        floatingLabelText: label || field,
        errorText: (props.hasErrors || dirty) ? get(model, `${field}.errors[0]`) : undefined,
        isDirty: props.hasErrors || dirty,
        menuItems: getListItems(field),
        onUpdate: handleUpdate(field)
    };

    if (field === 'user.zip') {
      merge(attributes, {type: 'number', maxLength: 5});
    }

    if (field === 'user.email') {
      merge(attributes, {maxLength: 255});
      if (emailDuplicate) merge(attributes, {errorText: 'Email is duplicate'});
    }

    const renderer = getFieldRenderer(fieldType);

    return renderer(attributes);

  }

  function renderColumn(field) {
    const currentIndex = mFields.findIndex(f => f === field);
    return (
      <div className='col expand'>
      {
        renderField(field, mLabels[currentIndex], model)
      }
      </div>
    );
  }

  function renderRow(rowFields = []) {
    return (
      <div className='row'>
      {
        rowFields.map(field => renderColumn(field))
      }
      </div>
    );
  }

  return (

    <div className='add-contact-component'>
      <div className='grid'>
      {
        fieldGroups.map(group => renderRow(group))
      }
      </div>
      <div className='actions'>
        <Button
          label='Cancel'
          className='button-secondary'
          onClick={handleCancel}
        />
        <Button
          label='Save'
          disabled={disableSave}
          onClick={handleSave}
        />
      </div>
    </div>
  );
}

AddContact.propTypes = {
    model: PropTypes.object.isRequired,
    staticData: PropTypes.object.isRequired,
    fields: PropTypes.object.isRequired,
    labels: PropTypes.array.isRequired,
    disableSave: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    columns: PropTypes.number.isRequired,
    emailDuplicate: PropTypes.bool,
};

AddContact.defaultProps = {
    model: {},
    fields: ['user.firstName', 'user.lastName', 'user.email', 'profile.types'], // Determines order in which they appear
    labels: ['First Name', 'Last Name', 'Email', 'Type of Contact'], // Must be in the same order as fields
    staticData: { // Is possible (?)
      contactTypes: [],
      timezoneList: [],
      contactPhoneTypes: []
    },
    disableSave: false,
    columns: 3 // Determines how many inputs per row
};



// WEBPACK FOOTER //
// ./shared/common/components/AddContact.js